import React, { useState, useEffect, useCallback } from "react";
import { Radio, Spin, Alert, Space } from "antd";
import { LoadingOutlined, SoundOutlined } from "@ant-design/icons";
import api from "@/api/index";
import type { AudioTrack } from "@/types/AudioTrack";

const LANGUAGE_NAMES: Record<string, string> = {
    rus: "Русский",
    eng: "Английский",
    fra: "Французский",
    deu: "Немецкий",
    ita: "Итальянский",
    spa: "Испанский",
    jpn: "Японский",
    kor: "Корейский",
    chi: "Китайский",
    und: "Неизвестный",
};

function formatLanguage(lang: string): string {
    return LANGUAGE_NAMES[lang] || lang.toUpperCase();
}

function formatTrackLabel(track: AudioTrack): string {
    const parts: string[] = [];
    parts.push(`#${track.index}`);
    parts.push(formatLanguage(track.language));

    if (track.codec) {
        parts.push(track.codec.toUpperCase());
    }
    if (track.channels > 0) {
        parts.push(track.channels === 1 ? "Mono" : track.channels === 2 ? "Stereo" : `${track.channels}ch`);
    }
    if (track.title) {
        parts.push(`"${track.title}"`);
    }
    if (track.is_default) {
        parts.push("(default)");
    }

    return parts.join(" ");
}

interface Props {
    filePath: string;
    selectedTrackIndex: number | null;
    onTrackSelect: (index: number) => void;
    disabled?: boolean;
}

function AudioTrackSelector({ filePath, selectedTrackIndex, onTrackSelect, disabled }: Props) {
    const [tracks, setTracks] = useState<AudioTrack[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [analysedPath, setAnalysedPath] = useState<string | null>(null);

    const fetchTracks = useCallback(async () => {
        if (!filePath || filePath === analysedPath) {
            return;
        }

        setLoading(true);
        setError(null);
        setTracks([]);

        try {
            const response = await api.files.getAudioTracks(filePath);
            const data = response?.data?.data || response?.data;
            const audioTracks: AudioTrack[] = data?.audio_tracks || [];

            setTracks(audioTracks);
            setAnalysedPath(filePath);

            if (audioTracks.length === 1) {
                onTrackSelect(audioTracks[0].index);
            }
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to analyze audio tracks";
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [filePath, analysedPath, onTrackSelect]);

    useEffect(() => {
        if (filePath) {
            fetchTracks();
        }
    }, [filePath, fetchTracks]);

    // Reset if file changes
    useEffect(() => {
        setAnalysedPath(null);
        setTracks([]);
        setError(null);
    }, [filePath]);

    if (!filePath) {
        return null;
    }

    if (loading) {
        return (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
                <Spin size="small" indicator={<LoadingOutlined />} />
                <span style={{ marginLeft: 8, color: "#888" }}>Анализ аудио-дорожек...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
                <Alert
                    type="warning"
                    message={error}
                    showIcon
                    style={{ fontSize: 12 }}
                />
            </div>
        );
    }

    if (tracks.length === 0) {
        return null;
    }

    if (tracks.length === 1) {
        return (
            <div style={{ marginTop: 8, marginBottom: 8, color: "#888", fontSize: 12 }}>
                <SoundOutlined style={{ marginRight: 4 }} />
                Файл содержит одну аудио-дорожку: {formatTrackLabel(tracks[0])}
            </div>
        );
    }

    return (
        <div style={{ marginTop: 8, marginBottom: 8 }}>
            <div style={{ marginBottom: 4, color: "#555", fontSize: 12 }}>
                <SoundOutlined style={{ marginRight: 4 }} />
                Аудио-дорожек в файле: <strong>{tracks.length}</strong>
            </div>
            <Radio.Group
                onChange={(e) => onTrackSelect(e.target.value)}
                value={selectedTrackIndex}
                disabled={disabled}
            >
                <Space direction="vertical" size={2}>
                    {tracks.map((track) => (
                        <Radio key={track.index} value={track.index} style={{ fontSize: 12 }}>
                            {formatTrackLabel(track)}
                        </Radio>
                    ))}
                </Space>
            </Radio.Group>
        </div>
    );
}

export default AudioTrackSelector;
